import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase.js';
import { useAuth } from '../lib/auth.jsx';

// CollaborationModal — shared modal for "Transfer ticket" and "Add helper" flows.
//
// Two modes:
//   - mode="transfer"  → calls Cloud Function transferTicket
//   - mode="helper"    → calls Cloud Function addHelperToTicket
//
// Props:
//   - mode: 'transfer' | 'helper'
//   - ticket: ticket doc with at least { id, status, assignedTo, helpers? }
//   - onClose(): close the modal
//   - onDone(): called after a successful action, in addition to onClose()
//
// Behaviour:
//   - Pulls every responder + vendor_admin in the current vendor and lets the user
//     pick one. Excludes themselves, the current assignee (for transfer this would
//     be a no-op; for helper this would conflict), and anyone already a helper
//     on this ticket.
//   - Optional 300-char "note" forwarded as the `message` payload — surfaces in the
//     escalationLog and the SMS body so the recipient gets context.
//   - Shows server-side errors inline. Disables the submit while in-flight.

export default function CollaborationModal({ mode, ticket, onClose, onDone }) {
  const { user, claims } = useAuth();
  const vendorId = claims?.vendor_id;

  const [responders, setResponders] = useState([]);
  const [selectedUid, setSelectedUid] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Pull the list of responders + admins in the vendor.
  useEffect(() => {
    if (!vendorId) return;
    const q = query(
      collection(db, 'vendors', vendorId, 'users'),
      where('role', 'in', ['responder', 'vendor_admin']),
    );
    const unsub = onSnapshot(q, (snap) => {
      const rows = snap.docs
        .map((d) => ({ uid: d.id, ...d.data() }))
        .filter((r) => !r.deletedAt);
      setResponders(rows);
    });
    return () => unsub();
  }, [vendorId]);

  const currentAssigneeUid = ticket?.assignedTo?.uid;
  const existingHelperUids = Array.isArray(ticket?.helpers) ? ticket.helpers : [];

  // Eligible picker list — excludes me, the current assignee, and existing helpers.
  // For transfer mode we also exclude existing helpers (you'd lose them by transferring
  // since helpers don't auto-promote — leave that decision to the user, but at minimum
  // don't surface them as "available transfer target" since the intent is hand-off
  // not promotion).
  const eligible = responders.filter((r) => {
    if (r.uid === user?.uid) return false;
    if (r.uid === currentAssigneeUid) return false;
    if (existingHelperUids.includes(r.uid)) return false;
    return true;
  });

  async function submit() {
    setErr(null);
    if (!selectedUid) {
      setErr('Pick a responder.');
      return;
    }
    setBusy(true);
    try {
      if (mode === 'transfer') {
        await httpsCallable(functions, 'transferTicket')({
          ticketId: ticket.id,
          toResponderUid: selectedUid,
          message: message.trim(),
        });
      } else {
        await httpsCallable(functions, 'addHelperToTicket')({
          ticketId: ticket.id,
          helperResponderUid: selectedUid,
          message: message.trim(),
        });
      }
      if (typeof onDone === 'function') onDone();
      onClose();
    } catch (e) {
      setErr(prettyError(e));
    } finally {
      setBusy(false);
    }
  }

  const title = mode === 'transfer' ? 'Transfer ticket' : 'Add helper';
  const submitLabel = mode === 'transfer' ? 'Transfer' : 'Add helper';
  const blurb = mode === 'transfer'
    ? `Hand this ticket off to another responder. They become the assignee — you stay on the audit log.`
    : `Add another responder as a helper. They get push + SMS notification and will see this ticket in their queue alongside yours.`;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{title}</h3>
        <p className="subtitle" style={{ marginTop: 0 }}>{blurb}</p>

        <label htmlFor="collab-select">Responder</label>
        <select
          id="collab-select"
          value={selectedUid}
          onChange={(e) => setSelectedUid(e.target.value)}
          disabled={busy}
        >
          <option value="">-- pick someone --</option>
          {eligible.map((r) => (
            <option key={r.uid} value={r.uid}>
              {r.fullName || r.email} {r.role === 'vendor_admin' ? '(admin)' : ''}
            </option>
          ))}
        </select>
        {eligible.length === 0 && (
          <div className="placeholder" style={{ marginTop: 6, fontSize: 13 }}>
            No other responders eligible.
          </div>
        )}

        <label htmlFor="collab-message" style={{ marginTop: 12 }}>
          Note (optional)
        </label>
        <textarea
          id="collab-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={300}
          rows={3}
          placeholder={mode === 'transfer'
            ? 'Why you are transferring (visible in audit log + SMS)'
            : 'What you need help with (visible in audit log + SMS)'}
          disabled={busy}
        />
        <small style={{ color: 'var(--muted)' }}>{message.length}/300</small>

        {err && <div className="error" style={{ marginTop: 8 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button className="btn-ghost" onClick={onClose} disabled={busy} style={{ flex: 1 }}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || !selectedUid}
            style={{ flex: 1 }}
          >
            {busy ? 'Working...' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function prettyError(e) {
  const m = e?.message || '';
  if (m.includes('failed-precondition')) return 'Ticket is no longer in the right state.';
  if (m.includes('permission-denied')) return 'You are not the current assignee.';
  if (m.includes('already-exists')) return 'That person is already a helper on this ticket.';
  return m || 'Action failed.';
}
